import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

// Google Analytics 4 Tracking ID
const GA_MEASUREMENT_ID = 'G-XXXXXXXXXX' // Replace with your actual GA4 Measurement ID

export const initGA = () => {
  // Load Google Analytics script
  const script = document.createElement('script')
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  script.async = true
  document.head.appendChild(script)

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || []
  function gtag() {
    window.dataLayer.push(arguments)
  }
  window.gtag = gtag

  gtag('js', new Date())
  gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We'll send page views manually
    cookie_flags: 'SameSite=None;Secure'
  })
}

export const logPageView = (url, title) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
      page_title: title
    })
    window.gtag('event', 'page_view', {
      page_path: url,
      page_title: title
    })
  }
}

export const logEvent = (action, category, label, value) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value
    })
  }
}

// Custom event trackers
export const trackButtonClick = (buttonName, location) => {
  logEvent('button_click', 'User Interaction', `${buttonName} - ${location}`)
}

export const trackFormSubmit = (formName, success = true) => {
  logEvent('form_submit', 'Form', formName, success ? 1 : 0)
}

export const trackChatStart = (userName) => {
  logEvent('chat_start', 'Engagement', userName)
}

export const trackConsultationRequest = (service) => {
  logEvent('consultation_request', 'Lead Generation', service, 1)
}

export const trackContactForm = (source) => {
  logEvent('contact_form', 'Lead Generation', source, 1)
}

export const trackApplicationSubmit = (projectType) => {
  logEvent('application_submit', 'Lead Generation', projectType, 1)
}

export const trackReviewSubmit = (rating) => {
  logEvent('review_submit', 'Engagement', `${rating} stars`, rating)
}

export const trackServiceView = (serviceName) => {
  logEvent('service_view', 'Content', serviceName)
}

export const trackPortfolioView = (projectName) => {
  logEvent('portfolio_view', 'Content', projectName)
}

export const trackDownload = (fileName) => {
  logEvent('file_download', 'Content', fileName)
}

export const trackOutboundLink = (url) => {
  logEvent('outbound_link', 'Navigation', url)
}

export const trackVideoPlay = (videoName) => {
  logEvent('video_play', 'Engagement', videoName)
}

export const trackScrollDepth = (percentage) => {
  logEvent('scroll_depth', 'Engagement', `${percentage}%`, percentage)
}

// Hook for automatic page view tracking
export const usePageTracking = () => {
  const location = useLocation()

  useEffect(() => {
    const pageTitle = document.title
    logPageView(location.pathname + location.search, pageTitle)
  }, [location])
}

// E-commerce tracking (if needed)
export const trackPurchase = (transactionId, value, items) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: 'EUR',
      items: items
    })
  }
}

export const trackAddToCart = (itemName, price) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'add_to_cart', {
      currency: 'EUR',
      value: price,
      items: [{
        item_name: itemName,
        price: price
      }]
    })
  }
}

// User engagement metrics
export const trackTimeOnPage = (pageName, seconds) => {
  logEvent('time_on_page', 'Engagement', pageName, seconds)
}

export const trackError = (errorMessage, page) => {
  logEvent('error', 'Technical', `${page}: ${errorMessage}`)
}

export default {
  initGA,
  logPageView,
  logEvent,
  trackButtonClick,
  trackFormSubmit,
  trackChatStart,
  trackConsultationRequest,
  trackContactForm,
  trackApplicationSubmit,
  trackReviewSubmit,
  trackServiceView,
  trackPortfolioView,
  trackDownload,
  trackOutboundLink,
  usePageTracking
}
